import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "vendiz_token";

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Interceptor de resposta: quando o backend retorna 401/402/403 indicando
// que a conta/assinatura foi invalidada DEPOIS do login (ex: master desativou
// o tenant manualmente, assinatura venceu, trial encerrou), guardamos a
// mensagem em sessionStorage e disparamos auth:invalidated para o AuthContext
// derrubar a sessão e levar pro /login (que lê e exibe a mensagem).
// Exceção: o próprio endpoint de login retorna esses status — não interceptar.
const BLOCK_STORAGE_KEY = "gestaoexpert:auth_block";
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || "";
    const isLoginCall = url.includes("/auth/login");
    const detail = err.response?.data?.detail;
    const isBlockPayload =
      detail && typeof detail === "object"
      && ["trial_expired", "subscription_expired", "account_disabled"].includes(detail.error);

    // Bloqueio de assinatura/conta detectado em request autenticada
    if (!isLoginCall && getToken() && (status === 402 || (status === 403 && isBlockPayload))) {
      try {
        sessionStorage.setItem(BLOCK_STORAGE_KEY, JSON.stringify({
          kind: detail.error,
          message: detail.message || "",
          whatsapp: detail.support_whatsapp || "",
        }));
      } catch {}
      try {
        setToken(null);
        window.dispatchEvent(new CustomEvent("auth:invalidated"));
      } catch {}
    } else if (status === 401 && !isLoginCall && getToken()) {
      // Token zumbi (JWT_SECRET trocou, usuário deletado, etc.)
      try {
        setToken(null);
        window.dispatchEvent(new CustomEvent("auth:invalidated"));
      } catch {}
    }
    return Promise.reject(err);
  }
);

// Permite ao Login consumir a mensagem armazenada (e limpá-la em seguida)
export function consumeAuthBlock() {
  try {
    const raw = sessionStorage.getItem(BLOCK_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(BLOCK_STORAGE_KEY);
    return JSON.parse(raw);
  } catch { return null; }
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Mensagem amigável e específica considerando tipo de erro (rede, 5xx, etc).
// Usado em telas críticas (login) para diagnosticar melhor.
export function formatAxiosError(err) {
  // Sem resposta = problema de rede / CORS / backend offline
  if (!err.response) {
    if (err.code === "ECONNABORTED") return "A conexão demorou demais. Verifique sua internet e tente de novo.";
    return "Falha de conexão com o servidor. Verifique sua internet e tente novamente em alguns segundos.";
  }
  const s = err.response.status;
  if (s >= 500) return "O servidor está temporariamente indisponível. Aguarde alguns segundos e tente novamente.";
  if (s === 502 || s === 503 || s === 504) return "O sistema está sendo atualizado. Aguarde alguns segundos e tente novamente.";
  return formatApiErrorDetail(err.response.data?.detail);
}

export function brl(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));
}

// Remove a tag interna de fornecedor que adicionamos ao nome de produtos avulsos
// em Vendas (formato " [Fornecedor]" ou " [Fornecedor - obs]").
// O fornecedor é informação INTERNA da loja — NUNCA pode vazar para o cliente
// (recibo, link público de pagamento, catálogo, saldo pendente, etc.).
// Aceita a tag em qualquer posição do texto (fim ou meio), não apenas no final.
export function cleanItemName(name) {
  if (!name) return "";
  return String(name).replace(/\s*\[[^\[\]]*\]\s*/g, " ").replace(/\s+/g, " ").trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// Ordenação de produtos com destaque + prioridade por categoria
// ─────────────────────────────────────────────────────────────────────────────
const FEATURED_CATEGORY_PRIORITY = [
  "molinetes",
  "varas para molinetes",
  "varas para carretilha",
  "varas para carretilhas",
  "carretilhas",
  "massas",
  "pastilhas",
  "vestuário",
  "vestuario",
];

function _norm(s) {
  return (s || "").toString().toLowerCase().trim()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function categoryRank(name) {
  const n = _norm(name);
  if (!n) return 999;
  // exato
  const idx = FEATURED_CATEGORY_PRIORITY.findIndex(c => _norm(c) === n);
  if (idx >= 0) return idx;
  // começa com ou contém — match parcial
  const partial = FEATURED_CATEGORY_PRIORITY.findIndex(c => n.includes(_norm(c)) || _norm(c).includes(n));
  if (partial >= 0) return partial + 0.5;
  return 999;
}

/** Ordena: produtos destacados primeiro (com prioridade por categoria), depois o restante por nome. */
export function sortProducts(products, categoriesMap = {}) {
  const cats = Array.isArray(categoriesMap) ? Object.fromEntries(categoriesMap.map(c => [c.id, c])) : categoriesMap;
  return [...products].sort((a, b) => {
    const af = a.featured ? 1 : 0;
    const bf = b.featured ? 1 : 0;
    if (af !== bf) return bf - af; // destacado primeiro
    if (af && bf) {
      const ar = categoryRank(cats[a.category_id]?.name);
      const br = categoryRank(cats[b.category_id]?.name);
      if (ar !== br) return ar - br;
    }
    return (a.name || "").localeCompare(b.name || "", "pt-BR");
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Cache leve em memória para GET requests (simples, com invalidação em mutações).
// IMPORTANTE: NÃO entrega "stale" enquanto revalida em background — isso causava
// dados desatualizados na UI. Aqui é cache puro: se fresco, devolve. Se não,
// busca de verdade. Mutações (POST/PUT/PATCH/DELETE) limpam o cache.
// ─────────────────────────────────────────────────────────────────────────────
const _getCache = new Map(); // key -> { data, ts }
const CACHE_TTL = 30_000; // 30s

function cacheKey(url, params) {
  const qs = params ? Object.entries(params).sort().map(([k,v])=>`${k}=${v}`).join("&") : "";
  return `${url}?${qs}`;
}

export function invalidateApiCache() { _getCache.clear(); }

// Intercepta resposta de mutação para invalidar o cache
api.interceptors.response.use(
  (response) => {
    const m = (response.config?.method || "").toLowerCase();
    if (m && m !== "get" && m !== "head") _getCache.clear();
    return response;
  },
  (error) => Promise.reject(error)
);

// Intercepta requisição GET: usa cache fresh quando há, senão faz fetch real.
const originalGet = api.get.bind(api);
api.get = function cachedGet(url, config = {}) {
  if (config._cache === false) return originalGet(url, config);
  const key = cacheKey(url, config.params);
  const cached = _getCache.get(key);
  const now = Date.now();
  if (cached && (now - cached.ts) < CACHE_TTL) {
    return Promise.resolve({ data: cached.data, status: 200, statusText: "OK", headers: {}, config });
  }
  return originalGet(url, config).then((resp) => {
    _getCache.set(key, { data: resp.data, ts: Date.now() });
    return resp;
  });
};

// Comprime imagem para no máx maxSize px (mantém proporção) e qualidade JPEG.
// Se falhar (HEIC sem suporte / canvas muito grande), tenta novamente com tamanho menor.
// Retorna SEMPRE um data URL JPEG limitado a ~600 KB no pior caso.
export function fileToBase64(file, opts = {}) {
  const { maxSize = 1280, quality = 0.82, mimeType = "image/jpeg" } = opts;
  return new Promise((resolve, reject) => {
    if (!file) return resolve("");
    const isRaster = /^image\/(jpe?g|png|webp|heic|heif)$/i.test(file.type);
    if (!isRaster || file.size < 80 * 1024) {
      // Imagem já pequena ou tipo não rasterizável — usa cru
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
      return;
    }

    const tryCompress = (size, qual, fallbackOnError) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          try {
            let { width, height } = img;
            if (width > size || height > size) {
              const ratio = Math.min(size / width, size / height);
              width = Math.round(width * ratio);
              height = Math.round(height * ratio);
            }
            const canvas = document.createElement("canvas");
            canvas.width = width; canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            const out = canvas.toDataURL(mimeType, qual);
            // Sanity: se data URL > 800KB depois de compressão, comprime mais agressivo
            if (out.length > 800 * 1024 && size > 720) {
              return tryCompress(720, 0.7, false);
            }
            resolve(out);
          } catch (e) {
            if (fallbackOnError) tryCompress(720, 0.7, false);
            else reject(e);
          }
        };
        img.onerror = () => {
          // Decode falhou (HEIC sem suporte, formato exótico). Salva o cru bruto.
          if (fallbackOnError) {
            const reader2 = new FileReader();
            reader2.onload = () => resolve(reader2.result);
            reader2.onerror = reject;
            reader2.readAsDataURL(file);
          } else {
            reject(new Error("Falha ao decodificar imagem"));
          }
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    };
    tryCompress(maxSize, quality, true);
  });
}
